import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Download, FileText, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { resolveSatJobError } from "@/lib/satAuditErrors";
import {
  SatDownloadPanelIntro,
  SatDownloadPanelStack,
  SatDownloadSectionCard,
} from "@/components/sat/SatDownloadSectionCard";
import {
  FISCAL_DOCS_DELEGATED_ACCESS,
  FISCAL_DOCS_OWNER,
  isFiscalDocsDelegatedAccess,
} from "@/modules/sat/fiscalDocsOwnership";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";

function formatTsMx(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(String(iso).replace("Z", "+00:00"));
    if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16).replace("T", " ");
    return d.toLocaleString("es-MX", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso).slice(0, 16);
  }
}

function formatRemaining(seconds) {
  if (!seconds || seconds <= 0) return "";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m} min`;
}

function DocRow({
  docType,
  label,
  status,
  force,
  busy,
  onDownload,
  onDownloadFile,
  showTechnicalUi = false,
}) {
  const cd = status?.cooldown?.[docType] || {};
  const last = docType === "csf" ? status?.last_csf_download : status?.last_opinion_download;
  const fileInfo = status?.files?.[docType];
  const hasStoredPdf = Boolean(fileInfo?.exists || fileInfo?.download_available);
  const missingAfterSuccess = Boolean(fileInfo?.last_success_missing_file);
  const blocked = cd.cooldown_active && !force && hasStoredPdf;
  const rowBusy = busy === docType;
  const satUpdateDisabled =
    rowBusy || blocked || (status != null && !status?.fiel_configured);

  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50/50 px-4 py-3"
      data-testid={`fiscal-doc-row-${docType}`}
    >
      <div className="space-y-1">
        <p className="text-sm font-medium text-slate-800">{label}</p>
        <p className="text-xs text-slate-600">
          Última descarga exitosa: <strong>{formatTsMx(last)}</strong>
        </p>
        {hasStoredPdf && (
          <p className="text-xs text-emerald-800 font-medium">
            {showTechnicalUi ? (
              <>
                PDF guardado en el servidor
                {fileInfo?.storage_label ? ` (${fileInfo.storage_label})` : ""}
                — puede descargarlo a su PC en cualquier momento. Al obtener uno nuevo del SAT, se reemplaza la versión
                vigente.
              </>
            ) : (
              <>PDF guardado — puede descargarlo a su PC en cualquier momento.</>
            )}
          </p>
        )}
        {showTechnicalUi && missingAfterSuccess && (
          <p className="text-xs text-amber-800 font-medium">
            {fileInfo?.recovery_hint ||
              "El registro indica una descarga previa, pero no hay copia en esta base de datos. Si cambió de equipo o de conexión Mongo, use «Descargar desde SAT» para recuperarlo."}
          </p>
        )}
        {!showTechnicalUi && missingAfterSuccess && (
          <p className="text-xs text-amber-800 font-medium">
            No hay copia guardada. Use «Descargar desde SAT» para obtener el documento.
          </p>
        )}
        {showTechnicalUi && fileInfo?.disk_only_warning && (
          <p className="text-xs text-amber-800 font-medium">
            Solo en disco de este servidor — sincronizando copia durable en MongoDB… Si viaja a otra PC, use la misma
            base de datos (Atlas) en el `.env`.
          </p>
        )}
        {showTechnicalUi && fileInfo?.reconciled_to_mongo && (
          <p className="text-xs text-emerald-800 font-medium">
            Copia durable en MongoDB actualizada desde el disco local (disponible en otros equipos con la misma BD).
          </p>
        )}
        {blocked && (
          <p className="text-xs text-amber-700 font-medium">
            Nueva consulta al SAT disponible en{" "}
            {cd.cooldown_remaining_seconds ? formatRemaining(cd.cooldown_remaining_seconds) : "24h"}.
            El PDF guardado sigue disponible con «Descargar PDF».
          </p>
        )}
        {showTechnicalUi && hasStoredPdf && fileInfo?.path_hint && (
          <p className="text-[10px] text-slate-500 font-mono truncate max-w-md">{fileInfo.path_hint}</p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 shrink-0">
        {hasStoredPdf && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-sm border-emerald-300 text-emerald-950 hover:bg-emerald-50"
            disabled={rowBusy}
            onClick={() => onDownloadFile(docType)}
            data-testid={`fiscal-doc-download-file-${docType}`}
          >
            <Download className="w-4 h-4 mr-1.5" />
            Descargar PDF
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          variant={hasStoredPdf ? "outline" : "default"}
          className={
            hasStoredPdf
              ? "rounded-sm"
              : "rounded-sm bg-[#0F2D5C] hover:bg-[#0F2D5C]/90"
          }
          disabled={satUpdateDisabled}
          onClick={() => onDownload(docType)}
          data-testid={`fiscal-doc-download-${docType}`}
        >
          {rowBusy ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          {rowBusy
            ? "Descargando…"
            : hasStoredPdf
              ? "Actualizar desde SAT"
              : "Descargar desde SAT"}
        </Button>
      </div>
    </div>
  );
}

/**
 * Panel de descarga automatizada CSF y opinión de cumplimiento (Fase 7).
 * Dueño técnico: Visor SAT `/documentos-fiscales-sat`. Trámites usa `accessMode="delegated"`.
 */
const FISCAL_DOCS_DB_FP_KEY = "visor:fiscal_docs_db_fingerprint";

function FiscalDocsAccessContextBanner({ accessMode }) {
  if (isFiscalDocsDelegatedAccess(accessMode)) {
    return (
      <p
        className="rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-slate-800 leading-relaxed m-0"
        data-testid="fiscal-docs-delegated-access-banner"
        role="note"
      >
        <strong>Acceso desde Trámites.</strong> CSF y opinión se gestionan con el módulo{" "}
        <strong>{FISCAL_DOCS_OWNER.label}</strong> (Visor SAT). Misma funcionalidad y repositorio; no es un módulo
        independiente. Pantalla canónica:{" "}
        <Link to={FISCAL_DOCS_OWNER.route} className="text-[#0F2D5C] underline font-medium">
          {FISCAL_DOCS_OWNER.screenTitle}
        </Link>
        .
      </p>
    );
  }

  return (
    <p
      className="rounded-md border border-slate-200 bg-slate-50/80 px-4 py-3 text-sm text-slate-700 leading-relaxed m-0"
      data-testid="fiscal-docs-owner-banner"
      role="note"
    >
      <strong>Documentos fiscales SAT</strong> — pantalla canónica de CSF y opinión de cumplimiento. Acceso principal desde{" "}
      <Link to="/datos-maestros" className="text-[#0F2D5C] underline font-medium">
        Datos Maestros
      </Link>
      .
    </p>
  );
}

export default function SatFiscalDocsPanel({ clientId, compact = false, accessMode = "owner" }) {
  const showTechnicalUi = usePlatformDesigner();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [force, setForce] = useState(false);
  const [dbFingerprintMismatch, setDbFingerprintMismatch] = useState(false);
  const pollRef = useRef(null);

  const loadStatus = useCallback(async () => {
    if (!clientId) {
      setStatus(null);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/sat/fiscal-docs/${clientId}/status`);
      setStatus(data);
    } catch (e) {
      setStatus(null);
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cargar el estado");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadStatus();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [loadStatus]);

  useEffect(() => {
    const fp = status?.storage?.fingerprint;
    if (!fp) return;
    try {
      const prev = localStorage.getItem(FISCAL_DOCS_DB_FP_KEY);
      if (prev && prev !== fp) {
        setDbFingerprintMismatch(true);
      }
      localStorage.setItem(FISCAL_DOCS_DB_FP_KEY, fp);
    } catch {
      /* ignore */
    }
  }, [status?.storage?.fingerprint]);

  const storageBanner = useMemo(() => {
    const st = status?.storage;
    if (!st) return null;
    return {
      hostHint: st.host_hint,
      dbName: st.db_name,
      policy: st.policy,
      reconciled: Array.isArray(st.reconciled_now) ? st.reconciled_now : [],
    };
  }, [status?.storage]);

  const pollJob = (jobId, docType) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/sat/fiscal-docs/jobs/${jobId}`, {
          params: { client_id: clientId },
        });
        if (data.status === "success") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setBusy(null);
          toast.success(
            docType === "csf"
              ? "Constancia de situación fiscal actualizada"
              : "Opinión de cumplimiento actualizada",
          );
          loadStatus();
        } else if (data.status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setBusy(null);
          const parsed = resolveSatJobError(data);
          const msg = [
            parsed.userMessage || formatApiError(data.error),
            parsed.technicalSummary && parsed.technicalSummary !== parsed.userMessage
              ? parsed.technicalSummary
              : null,
          ]
            .filter(Boolean)
            .join(" — ");
          toast.error(
            [msg || "La descarga falló", "El PDF anterior guardado (si existía) se conserva."]
              .filter(Boolean)
              .join(" "),
          );
        }
      } catch {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setBusy(null);
      }
    }, 2000);
  };

  const onDownload = async (docType) => {
    if (!clientId) {
      toast.error("Selecciona un cliente");
      return;
    }
    setBusy(docType);
    try {
      const path = docType === "csf" ? "csf" : "opinion";
      const { data } = await api.post(`/sat/fiscal-docs/${clientId}/${path}`, null, {
        params: { force },
      });
      if (data.job_id) {
        pollJob(data.job_id, docType);
      } else {
        setBusy(null);
        loadStatus();
      }
    } catch (e) {
      setBusy(null);
      toast.error(formatApiError(e.response?.data?.detail) || "Error al iniciar descarga");
    }
  };

  const onDownloadFile = async (docType) => {
    if (!clientId) return;
    try {
      const { data, headers } = await api.get(`/sat/fiscal-docs/${clientId}/file/${docType}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      const cd = headers["content-disposition"] || "";
      const quoted = cd.match(/filename="([^"]+)"/i);
      a.download = quoted ? quoted[1] : `${docType}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo abrir el PDF");
    }
  };

  if (!clientId) {
    return (
      <SatDownloadPanelStack data-testid="sat-fiscal-docs-panel">
        {showTechnicalUi ? <FiscalDocsAccessContextBanner accessMode={accessMode} /> : null}
        {!compact && (
          <SatDownloadPanelIntro
            icon={FileText}
            iconWrapClassName="bg-slate-50 border-slate-200 text-[#0F2D5C]"
            title="Documentos fiscales"
            description={
              showTechnicalUi
                ? "Seleccione un cliente arriba para descargar constancia y opinión con la e.firma configurada."
                : "Seleccione un cliente para descargar constancia de situación fiscal y opinión de cumplimiento con su e.firma."
            }
          />
        )}
        <SatDownloadSectionCard title="Documentos disponibles">
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 m-0 mb-3">
            Seleccione un cliente en «Cliente de trabajo» para habilitar las descargas.
          </p>
          <div className="space-y-3">
            <DocRow
              docType="csf"
              label="Constancia de situación fiscal (CSF)"
              status={null}
              force={false}
              busy={null}
              onDownload={() => toast.error("Seleccione un cliente")}
              onDownloadFile={() => toast.error("Seleccione un cliente")}
            />
            <DocRow
              docType="opinion"
              label="Opinión positiva de cumplimiento (SAT)"
              status={null}
              force={false}
              busy={null}
              onDownload={() => toast.error("Seleccione un cliente")}
              onDownloadFile={() => toast.error("Seleccione un cliente")}
            />
          </div>
        </SatDownloadSectionCard>
      </SatDownloadPanelStack>
    );
  }

  return (
    <SatDownloadPanelStack data-testid="sat-fiscal-docs-panel">
      {showTechnicalUi ? <FiscalDocsAccessContextBanner accessMode={accessMode} /> : null}
      {!compact && (
        <SatDownloadPanelIntro
          icon={FileText}
          iconWrapClassName="bg-slate-50 border-slate-200 text-[#0F2D5C]"
          title="Documentos fiscales"
          description={
            showTechnicalUi ? (
              <>
                Descarga automatizada con la e.firma configurada en{" "}
                <Link
                  to={`/clientes?tab=credenciales&client=${encodeURIComponent(clientId)}`}
                  className="text-[#0F2D5C] underline font-medium"
                >
                  Clientes → Certificados y FIEL
                </Link>
                . Tras la primera descarga exitosa, el PDF queda en <strong>MongoDB</strong> (copia durable) y puede
                descargarlo a su PC cuando quiera, desde cualquier lugar con la misma base de datos. Intervalo mínimo de
                24 horas entre consultas nuevas al SAT (salvo forzar o si no hay copia guardada).
              </>
            ) : (
              <>
                Descarga automatizada con la e.firma del cliente. Tras la primera descarga exitosa puede descargar el
                PDF a su PC cuando lo necesite. Intervalo mínimo de 24 horas entre consultas nuevas al SAT (salvo
                forzar o si no hay copia guardada).
              </>
            )
          }
        />
      )}

      {showTechnicalUi && storageBanner && (
        <SatDownloadSectionCard
          title="Almacenamiento durable"
          className="border-slate-200 bg-slate-50/40"
        >
          <div data-testid="fiscal-docs-storage-banner">
          <div className="text-xs text-slate-700 space-y-2 leading-relaxed">
            <p className="m-0">
              <strong>Base de datos:</strong> {storageBanner.dbName} — {storageBanner.hostHint}
              {status?.storage?.mongo_primary ? (
                <span className="ml-1 text-emerald-800 font-medium">(modo nube: MongoDB es la copia oficial)</span>
              ) : null}
            </p>
            <p className="m-0 text-slate-600">{storageBanner.policy}</p>
            {storageBanner.reconciled.length > 0 && (
              <p className="m-0 text-emerald-800 font-medium">
                Sincronizado ahora a MongoDB: {storageBanner.reconciled.join(", ")}.
              </p>
            )}
            {dbFingerprintMismatch && (
              <p
                className="m-0 text-amber-900 font-medium bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5"
                data-testid="fiscal-docs-db-mismatch-warning"
              >
                Esta sesión usa una base de datos distinta a la anterior en este navegador. Los PDF descargados en
                otro equipo solo aparecen si ambos entornos comparten el mismo <code>MONGO_URL</code> y{" "}
                <code>DB_NAME</code> (p. ej. MongoDB Atlas).
              </p>
            )}
          </div>
          </div>
        </SatDownloadSectionCard>
      )}

      {status && !status.fiel_configured && (
        <SatDownloadSectionCard title="Requisito" className="border-amber-200 bg-amber-50/30">
          <div className="flex gap-2 text-xs text-amber-900">
            <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
            <span className="m-0">Configure la e.firma del cliente antes de descargar constancia u opinión.</span>
          </div>
        </SatDownloadSectionCard>
      )}

      <SatDownloadSectionCard title="Opciones de descarga">
        <div className="flex items-center gap-2 rounded-md border border-slate-100 bg-slate-50/80 px-3 py-2">
          <Checkbox
            id="fiscal-docs-force"
            checked={force}
            onCheckedChange={(v) => setForce(Boolean(v))}
            data-testid="fiscal-docs-force-checkbox"
          />
          <Label htmlFor="fiscal-docs-force" className="text-xs text-slate-700 cursor-pointer">
            Forzar nueva descarga (omitir cooldown de 24h)
          </Label>
        </div>
      </SatDownloadSectionCard>

      <SatDownloadSectionCard title="Documentos disponibles">
        {loading && !status ? (
          <div className="flex items-center gap-2 text-sm text-slate-600 py-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Cargando estado…
          </div>
        ) : (
          <div className="space-y-3">
            <DocRow
              docType="csf"
              label="Constancia de situación fiscal (CSF)"
              status={status}
              force={force}
              busy={busy}
              onDownload={onDownload}
              onDownloadFile={onDownloadFile}
              showTechnicalUi={showTechnicalUi}
            />
            <DocRow
              docType="opinion"
              label="Opinión positiva de cumplimiento (SAT)"
              status={status}
              force={force}
              busy={busy}
              onDownload={onDownload}
              onDownloadFile={onDownloadFile}
              showTechnicalUi={showTechnicalUi}
            />
          </div>
        )}
      </SatDownloadSectionCard>
    </SatDownloadPanelStack>
  );
}
